import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiX, FiAlertTriangle } = FiIcons;

const Comparison = () => {
  const comparisonData = [
    {
      category: "Brain Skills",
      weTB: { status: "check", text: "Strengthens cognitive control" },
      twelveStep: { status: "x", text: "Spiritual/social focus" },
      rehab: { status: "x", text: "Medical stabilization" },
      counseling: { status: "warning", text: "Behavioral strategies" }
    },
    {
      category: "Relapse Prevention",
      weTB: { status: "check", text: "25-70% reduction in relapse" },
      twelveStep: { status: "warning", text: "Variable success rates" },
      rehab: { status: "x", text: "High relapse post-discharge" },
      counseling: { status: "warning", text: "Depends on engagement" }
    },
    {
      category: "Individualized",
      weTB: { status: "check", text: "Targets your specific EF deficits" },
      twelveStep: { status: "x", text: "One-size-fits-all approach" },
      rehab: { status: "warning", text: "Group programming" },
      counseling: { status: "check", text: "Personalized counseling" }
    },
    {
      category: "Skill Building",
      weTB: { status: "check", text: "Builds lasting cognitive abilities" },
      twelveStep: { status: "x", text: "Relies on group support" },
      rehab: { status: "x", text: "Crisis intervention focus" },
      counseling: { status: "warning", text: "Coping strategies" }
    },
    {
      category: "Complements Treatment",
      weTB: { status: "check", text: "Enhances all other therapies" },
      twelveStep: { status: "check", text: "Can work with anything" },
      rehab: { status: "x", text: "Usually standalone" },
      counseling: { status: "check", text: "Works with other therapy" }
    },
    {
      category: "Investment",
      weTB: { status: "check", text: "$135-200/session" },
      twelveStep: { status: "check", text: "Often free" },
      rehab: { status: "x", text: "$5,000-30,000/month" },
      counseling: { status: "check", text: "$80-150/session" }
    },
    {
      category: "Insurance Coverage",
      weTB: { status: "check", text: "Often" },
      twelveStep: { status: "check", text: "" },
      rehab: { status: "warning", text: "Depends" },
      counseling: { status: "check", text: "Often" }
    },
    {
      category: "Timeline",
      weTB: { status: "check", text: "Skills improve in weeks" },
      twelveStep: { status: "x", text: "\"One day at a time\"" },
      rehab: { status: "x", text: "30-90 day programs" },
      counseling: { status: "warning", text: "Ongoing, variable progress" }
    }
  ];

  const getIcon = (status) => {
    switch (status) {
      case 'check':
        return <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />;
      case 'x':
        return <SafeIcon icon={FiX} className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'check':
        return 'bg-green-50 border-green-200';
      case 'x':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The Missing Piece in Your{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Recovery Toolkit
            </span>
          </h2>
          <p className="text-xl text-gray-600">We The Billions vs. Traditional Recovery Approaches</p>
        </motion.div>

        {/* Desktop Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="hidden lg:block overflow-x-auto"
        >
          <table className="w-full bg-white rounded-3xl shadow-lg overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-accent-magenta to-accent-purple text-white">
                <th className="p-6 text-left font-playfair text-xl">Approach</th>
                <th className="p-6 text-center font-playfair text-xl">We The Billions<br />EF Therapy</th>
                <th className="p-6 text-center font-playfair text-xl">12-Step Programs<br />Only</th>
                <th className="p-6 text-center font-playfair text-xl">Rehab/Detox<br />Only</th>
                <th className="p-6 text-center font-playfair text-xl">Addiction<br />Counseling</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  viewport={{ once: true }}
                  className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                >
                  <td className="p-6 font-semibold text-gray-900">{row.category}</td>
                  <td className={`p-6 text-center border-l-4 border-accent-magenta ${getStatusColor(row.weTB.status)}`}>
                    <div className="flex items-center justify-center gap-2">
                      {getIcon(row.weTB.status)}
                      <span className="text-sm">{row.weTB.text}</span>
                    </div>
                  </td>
                  <td className={`p-6 text-center ${getStatusColor(row.twelveStep.status)}`}>
                    <div className="flex items-center justify-center gap-2">
                      {getIcon(row.twelveStep.status)}
                      <span className="text-sm">{row.twelveStep.text}</span>
                    </div>
                  </td>
                  <td className={`p-6 text-center ${getStatusColor(row.rehab.status)}`}>
                    <div className="flex items-center justify-center gap-2">
                      {getIcon(row.rehab.status)}
                      <span className="text-sm">{row.rehab.text}</span>
                    </div>
                  </td>
                  <td className={`p-6 text-center ${getStatusColor(row.counseling.status)}`}>
                    <div className="flex items-center justify-center gap-2">
                      {getIcon(row.counseling.status)}
                      <span className="text-sm">{row.counseling.text}</span>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-8">
          {comparisonData.map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-lg p-6"
            >
              <h3 className="text-xl font-playfair font-bold text-gray-900 mb-6 text-center">
                {row.category}
              </h3>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-2xl border-l-4 border-accent-magenta ${getStatusColor(row.weTB.status)}`}>
                  <div className="font-semibold text-accent-magenta mb-2">We The Billions EF Therapy</div>
                  <div className="flex items-center gap-2">
                    {getIcon(row.weTB.status)}
                    <span className="text-sm">{row.weTB.text}</span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-2xl ${getStatusColor(row.twelveStep.status)}`}>
                  <div className="font-semibold text-gray-700 mb-2">12-Step Programs Only</div>
                  <div className="flex items-center gap-2">
                    {getIcon(row.twelveStep.status)}
                    <span className="text-sm">{row.twelveStep.text}</span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-2xl ${getStatusColor(row.rehab.status)}`}>
                  <div className="font-semibold text-gray-700 mb-2">Rehab/Detox Only</div>
                  <div className="flex items-center gap-2">
                    {getIcon(row.rehab.status)}
                    <span className="text-sm">{row.rehab.text}</span>
                  </div>
                </div>
                
                <div className={`p-4 rounded-2xl ${getStatusColor(row.counseling.status)}`}>
                  <div className="font-semibold text-gray-700 mb-2">Addiction Counseling</div>
                  <div className="flex items-center gap-2">
                    {getIcon(row.counseling.status)}
                    <span className="text-sm">{row.counseling.text}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Comparison;